// Loot spawner (Phase 2 payoff): rolls the 2 000+ template loot-spots that
// buildings.lootSites already carries against catalog/loot-tables.json.
// Registry ids (itm.food.canned-peas, clo.torso.flannel-shirt-red…) map onto
// the playable item catalog by the keyword rules in ./loot-core.mjs, which
// also assembles the tables and holds the rare bonus finds; ids with no
// playable counterpart yet simply never spawn (their weight is skipped, not
// wasted on junk placeholders). Rolls are seeded per spot → the same session
// always finds the same loot in the same kitchen; looted spots stay empty.
// Rendering + G-pickup mirrors forage.js: thin-instanced templates streamed
// around the player.
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { Vector3, Matrix, Quaternion, Color3 } from '@babylonjs/core/Maths/math';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { ITEMS } from '../game/items';
// Pure, Node-testable half of this file (id rules, table assembly, the
// list-removal guard): tools/invtest.mjs measures the economy through it.
import { buildTables, findRef, takeRef } from './loot-core.mjs';

// ---- infected corpse pockets (docs/INFECTED.md Z132…Z137) ----------------
// What was in their pockets when it happened, by the loot tag of the place
// they died in. Rags dominate everywhere — clothing off a body is the one
// thing every corpse has — and they are dirty enough to seed an infection.
const CORPSE_TABLES = {
  civilian: [['rags', 22], ['wallet', 8], ['cigarettes', 6], ['crackers', 5], ['candy_bar', 4],
    ['canned_veg', 3], ['soda_can', 3], ['bandage', 3], ['painkillers', 2], ['duct_tape', 2],
    ['pocket_watch', 1.5], ['ammo_pistol', 1]],
  residential: [['rags', 22], ['wallet', 7], ['crackers', 5], ['canned_soup', 4], ['juice_can', 4],
    ['cigarettes', 4], ['bandage', 3], ['matches', 2], ['duct_tape', 2], ['pocket_watch', 1.5]],
  military: [['rags', 14], ['ammo_rifle', 8], ['ammo_pistol', 5], ['canned_stew', 5], ['bandage', 5],
    ['painkillers', 3], ['duct_tape', 3], ['cigarettes', 3], ['antibiotics', 1.5]],
  police: [['rags', 16], ['ammo_pistol', 8], ['bandage', 4], ['wallet', 4], ['cigarettes', 4],
    ['painkillers', 3], ['duct_tape', 2], ['ammo_rifle', 2]],
  medical: [['rags', 16], ['bandage', 10], ['painkillers', 7], ['antibiotics', 4], ['wallet', 3],
    ['crackers', 3]],
  industrial: [['rags', 20], ['duct_tape', 8], ['cigarettes', 5], ['wallet', 4], ['crackers', 4],
    ['canned_fish', 3], ['matches', 2]],
};
CORPSE_TABLES.fuel = CORPSE_TABLES.vehicle = CORPSE_TABLES.industrial;
CORPSE_TABLES.emergency = CORPSE_TABLES.police;
const CORPSE = {
  emptyChance: 0.18,   // some of them really are just a torn shirt
  second: 0.35,        // chance of a second item
  third: 0.10,         // …and a third
  reach: 2.4,          // same arm's length as the animal butcher path
  noise: 8,            // m — rummaging a body is quiet but not silent
};

// ---- loot respawn (Z138…Z141) -------------------------------------------
const RESPAWN = {
  ms: 30 * 60 * 1000,  // a cleared town refills over half an hour
  jitter: 0.35,        // ±35 % per spot so it does not all pop back at once
  blockDist: 45,       // m — never refill a shelf the player is standing at
  sweepS: 5,           // how often the sweep runs
};

// mesh template per rough item shape
function shapeFor(id) {
  const def = ITEMS[id];
  if (def.kind === 'backpack') return 'pack';
  if (def.kind === 'shirt' || def.kind === 'jacket' || def.kind === 'pants') return 'cloth';
  if (def.kind === 'med') return 'med';
  if (def.kind === 'drink') return 'bottle';
  if (id.startsWith('canned') || id === 'bulging_can') return 'can';
  return 'box';
}

function hash2(x, z, salt) {
  let h = (x * 374761393 + z * 668265263 + salt * 971) | 0;
  h = (h ^ (h >> 13)) * 1274126177;
  return (((h ^ (h >> 16)) >>> 0) % 100000) / 100000;
}

// infected ids are integers (Z19) but wolves/dummies use strings — fold both
// into the same deterministic 0..1 stream so a corpse always holds the same
// pockets on every client without a byte of network traffic
function hashId(id, salt) {
  if (typeof id === 'number') return hash2(id | 0, (id * 31) | 0, salt);
  const s = String(id);
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return hash2(h, h >> 7, salt);
}

export class Loot {
  constructor(scene, terrain, buildings) {
    this.scene = scene;
    this.terrain = terrain;
    this.buildings = buildings;
    this.tables = null;       // tag → {rolls, entries: [{id (mapped), w}]}
    this.taken = new Set();   // "siteIdx_spotIdx_n" looted this session
    this.takenAt = new Map(); // key → ms when it was emptied (null = server truth, age unknown)
    this.items = [];          // spawned near player: {key, id, qty, x, y, z}
    this.lastBuild = new Vector3(1e9, 0, 1e9);
    this.templates = new Map();
    this.noise = null;        // NoiseSystem — rummaging is a 6 m ping (Z27)
    this.respawnMs = RESPAWN.ms;
    this._sweepT = 0;
    // infected corpses registered by src/world/zombies.js: id → {id,x,z,tag}
    this.corpses = new Map();
    this.searched = new Set(); // corpse ids already emptied
    // What a PARTIAL search left on the body: id → [{id,qty}]. Without it the
    // remainder was neither kept nor re-rollable — searchCorpse marked the
    // body emptied and the stacks that did not fit were destroyed.
    this.corpseLeft = new Map();
    // Ids of bodies that have been OPENED. A corpse row streams out at 220 m
    // (INF.stream.snapshot) and streams back in as `state: -1`, which makes
    // zombies.js fire _onDeath → registerCorpse a second time; without this an
    // already-searched body re-rolled its deterministic pockets (corpseLoot is
    // seeded on the id alone) and handed the player the SAME stacks again,
    // repeatable until it rotted. A body that has been opened can never
    // re-roll, whatever happens to the remainder.
    this.partial = new Set();
    this._initMeshes();
    this._load();
  }

  async _load() {
    try {
      const raw = await (await fetch('assets/map10k/catalog/loot-tables.json')).json();
      // buildTables owns the id mapping AND the rare bonus finds. It creates a
      // table even when no registry entry mapped into it, which is what the
      // old `tables.X?.entries.push(...)` pattern here could not do: military,
      // industrial and infrastructure did not exist, so their pushes — the
      // expedition rucksack, the purification tablets, the duct tape — were
      // dropped on the floor and roughly a fifth of all building loot spots
      // returned nothing (B5).
      this.tables = buildTables(raw, ITEMS);
      this.lastBuild.set(1e9, 0, 1e9); // roll once tables are in
    } catch (e) {
      console.warn('loot tables failed:', e.message);
      this.tables = {};
    }
  }

  _mat(name, hex) {
    const m = new StandardMaterial(name, this.scene);
    m.diffuseColor = Color3.FromHexString(hex);
    m.emissiveColor = m.diffuseColor.scale(0.15);
    m.specularColor = Color3.Black();
    return m;
  }

  _initMeshes() {
    const S = this.scene;
    const tpl = (name, mesh, hex) => {
      mesh.material = this._mat('lm_' + name, hex);
      mesh.setEnabled(false);
      mesh.isPickable = false;
      this.templates.set(name, [mesh]);
    };
    const can = MeshBuilder.CreateCylinder('loot_can', { height: 0.12, diameter: 0.085, tessellation: 8 }, S);
    can.position.y = 0.06; can.bakeCurrentTransformIntoVertices();
    tpl('can', can, '#9aa0a2');
    const box = MeshBuilder.CreateBox('loot_box', { width: 0.16, height: 0.07, depth: 0.11 }, S);
    box.position.y = 0.035; box.bakeCurrentTransformIntoVertices();
    tpl('box', box, '#b08a52');
    const bottle = MeshBuilder.CreateCylinder('loot_bottle', { height: 0.26, diameterBottom: 0.08, diameterTop: 0.035, tessellation: 7 }, S);
    bottle.position.y = 0.13; bottle.bakeCurrentTransformIntoVertices();
    tpl('bottle', bottle, '#5c7a4a');
    const med = MeshBuilder.CreateBox('loot_med', { width: 0.14, height: 0.06, depth: 0.1 }, S);
    med.position.y = 0.03; med.bakeCurrentTransformIntoVertices();
    tpl('med', med, '#d8d8d0');
    const cloth = MeshBuilder.CreateBox('loot_cloth', { width: 0.34, height: 0.09, depth: 0.28 }, S);
    cloth.position.y = 0.045; cloth.bakeCurrentTransformIntoVertices();
    tpl('cloth', cloth, '#5a6a7a');
    const pack = MeshBuilder.CreateBox('loot_pack', { width: 0.34, height: 0.4, depth: 0.2 }, S);
    pack.position.y = 0.2; pack.bakeCurrentTransformIntoVertices();
    tpl('pack', pack, '#6a5a3a');
  }

  // deterministic roll of one spot; returns [{key,id,qty,x,y,z}]
  rollSpot(si, pi, spot) {
    const t = this.tables[spot.tag];
    if (!t) return [];
    if (hash2(si * 7 + 1, pi * 13 + 2, 21) > 0.55) return []; // many spots are bare
    const [rMin, rMax] = t.rolls;
    const n = Math.min(3, rMin + Math.floor(hash2(si, pi, 22) * (rMax - rMin + 1)));
    const out = [];
    let tw = 0;
    for (const e of t.entries) tw += e.w;
    for (let k = 0; k < n; k++) {
      let r = hash2(si * 3, pi * 5, 30 + k) * tw;
      const e = t.entries.find((en) => (r -= en.w) <= 0) || t.entries[0];
      const def = ITEMS[e.id];
      const qty = def.stack > 1 && hash2(si, pi, 40 + k) < 0.3 ? 2 : 1;
      const a = hash2(si, pi, 50 + k) * Math.PI * 2;
      const rr = 0.15 + hash2(si, pi, 60 + k) * 0.45;
      const x = spot.x + Math.sin(a) * rr;
      const z = spot.z + Math.cos(a) * rr;
      const y = this.buildings.floorAt(x, z) ?? this.terrain.getHeight(x, z);
      out.push({ key: `${si}_${pi}_${k}`, id: e.id, qty, x, y: y + 0.02, z });
    }
    return out;
  }

  rebuild(focus) {
    const items = [];
    const sites = this.buildings.lootSites;
    for (let si = 0; si < sites.length; si++) {
      const s = sites[si];
      if (!s.spots || Math.hypot(s.x - focus.x, s.z - focus.z) > 160) continue;
      for (let pi = 0; pi < s.spots.length; pi++) {
        for (const it of this.rollSpot(si, pi, s.spots[pi])) {
          if (!this.taken.has(it.key)) items.push(it);
        }
      }
    }
    this.items = items;
    const per = new Map();
    for (const it of items) {
      const shape = shapeFor(it.id);
      if (!per.has(shape)) per.set(shape, []);
      const rot = hash2(Math.floor(it.x * 9), Math.floor(it.z * 9), 71) * Math.PI * 2;
      per.get(shape).push(Matrix.Compose(Vector3.One(), Quaternion.FromEulerAngles(0, rot, 0), new Vector3(it.x, it.y, it.z)));
    }
    for (const [name, meshes] of this.templates) {
      const mats = per.get(name);
      for (const mesh of meshes) {
        if (!mats || !mats.length) { mesh.setEnabled(false); continue; }
        mesh.setEnabled(true);
        const buf = new Float32Array(mats.length * 16);
        mats.forEach((m, i) => m.copyToArray(buf, i * 16));
        mesh.thinInstanceSetBuffer('matrix', buf, 16, false);
        mesh.thinInstanceRefreshBoundingInfo();
      }
    }
  }

  nearest(pos, reach = 2.2) {
    let best = null, bestD = reach;
    for (const it of this.items) {
      const d = Math.hypot(it.x - pos.x, it.z - pos.z);
      if (d < bestD) { best = it; bestD = d; }
    }
    return best;
  }

  label(it) {
    return `Take ${ITEMS[it.id].name}${it.qty > 1 ? ' ×' + it.qty : ''}`;
  }

  pick(it, inventory) {
    // Resolve the reference against the live list FIRST. A stale `it` (a
    // rebuild swapped the array out, or someone else emptied the spot) used to
    // reach `splice(indexOf(it), 1)` with -1, which deletes the LAST entry in
    // the list instead of the intended one — a pickup that made an unrelated
    // shelf disappear (A3). forage.applyGroundTake always guarded this; now
    // both paths share one guard.
    const live = findRef(this.items, it);
    if (!live) return { ok: false, msg: 'Already taken' };
    // acquire(), not addItem(): a pack too big for any pocket is still a real
    // find, so a strict upgrade goes straight on your back (B6). Either way
    // the boolean is honest now, so a refusal really does leave the spot full
    // instead of eating the item (A2).
    const got = inventory.acquire(live.id, live.qty);
    if (!got.ok) return { ok: false, msg: 'Inventory full' };
    this.taken.add(live.key);
    this.takenAt.set(live.key, this._now());
    takeRef(this.items, live);
    this.lastBuild.set(1e9, 0, 1e9);
    this.noise?.rustle(live.x, live.z); // a can lifted off a shelf carries ~6 m
    this.onNet?.({ k: 'loot', key: live.key });
    const name = ITEMS[live.id].name;
    return {
      ok: true,
      msg: got.equipped ? `Put on the ${name}` : `Took ${name}${live.qty > 1 ? ' ×' + live.qty : ''}`,
    };
  }

  // multiplayer: someone else emptied this spot
  applyTake(key) {
    this.taken.add(key);
    this.takenAt.set(key, this._now()); // we watched it happen: the clock starts now
    const i = this.items.findIndex((it) => it.key === key);
    if (i >= 0) { this.items.splice(i, 1); this.lastBuild.set(1e9, 0, 1e9); }
  }

  // ---- loot respawn (Z138…Z141) ------------------------------------------
  // Server-truth aware: keys that arrive in the join snapshot have no known
  // age (world.json stores bare keys), so they are marked `null` and NEVER
  // expire on the client — only spots we watched being emptied this session
  // run a timer. That way two clients can never disagree into a double-spawn;
  // the shared expiry belongs on the server (TODO list in docs/INFECTED.md).
  applyWorldTaken(list) {
    for (const e of list || []) {
      if (typeof e === 'string') { this.taken.add(e); this.takenAt.set(e, null); continue; }
      if (!e || !e.key) continue;
      this.taken.add(e.key);
      // {key, t}: t = seconds ago it was taken (server-supplied, once it can)
      this.takenAt.set(e.key, Number.isFinite(e.t) ? this._now() - e.t * 1000 : null);
    }
    this.lastBuild.set(1e9, 0, 1e9);
  }

  _now() { return typeof performance !== 'undefined' ? performance.now() : Date.now(); }

  // where a loot key lives, so a spot never refills in front of the player
  _keyPos(key) {
    const si = parseInt(key, 10);
    const s = this.buildings?.lootSites?.[si];
    return s ? { x: s.x, z: s.z } : null;
  }

  _sweepRespawn(focus, dt) {
    this._sweepT -= dt;
    if (this._sweepT > 0) return 0;
    this._sweepT = RESPAWN.sweepS;
    const now = this._now();
    let back = 0;
    for (const [key, at] of this.takenAt) {
      if (at == null) continue;                       // server truth, age unknown
      const life = this.respawnMs * (1 + (hashId(key, 77) - 0.5) * 2 * RESPAWN.jitter);
      if (now - at < life) continue;
      const p = this._keyPos(key);
      if (p && focus && Math.hypot(p.x - focus.x, p.z - focus.z) < RESPAWN.blockDist) continue;
      this.taken.delete(key);
      this.takenAt.delete(key);
      // NOT a bare relay: mp-server.mjs:772 deletes the key from S.loot and
      // marks the world dirty, so this client-side sweep is authoritative over
      // shared, persisted state. (The comment here used to say the server
      // ignored it, which is how the next reader mis-reads the respawn economy.)
      this.onNet?.({ k: 'loot-respawn', key });
      back++;
    }
    if (back) this.lastBuild.set(1e9, 0, 1e9);        // force a rebuild: the shelf is stocked again
    return back;
  }

  // ---- infected corpses (Z132…Z137) --------------------------------------
  // src/world/zombies.js owns the bodies; loot owns what is in their pockets.
  // Same G-interact contract as animals.nearestCorpse/butcher.
  registerCorpse(c) {
    if (!c || c.id == null) return null;
    const e = { id: c.id, x: c.x, z: c.z, tag: c.tag || this.corpseTagAt(c.x, c.z) };
    this.corpses.set(c.id, e);
    return e;
  }

  // Where did it fall? The snapshot row carries no tag (and under server truth
  // there is no local slot to ask), so the pockets are decided by the nearest
  // loot site — a body on a barracks floor is worth more than one in a shed.
  corpseTagAt(x, z, radius = 40) {
    const sites = this.buildings?.lootSites;
    if (!sites?.length) return 'civilian';
    let best = null, bestD = radius * radius;
    for (const s of sites) {
      const dx = s.x - x, dz = s.z - z;
      const d = dx * dx + dz * dz;
      if (d < bestD) { bestD = d; best = s; }
    }
    if (!best) return 'civilian';
    for (const t of best.tags || []) if (CORPSE_TABLES[t]) return t;
    return 'civilian';
  }

  // A corpse leaving the snapshot radius takes its RENDERED row with it, not
  // the player's property: deleting corpseLeft here destroyed the remainder
  // that searchCorpse had just promised to keep ("more still on the body"),
  // on any ordinary 220 m walk. It is one small array per partially-searched
  // body and registerCorpse finds it again when the row returns.
  dropCorpse(id) { this.corpses.delete(id); }

  nearestCorpse(pos, reach = CORPSE.reach) {
    let best = null, bestD = reach;
    for (const c of this.corpses.values()) {
      if (this.searched.has(c.id)) continue;
      const d = Math.hypot(c.x - pos.x, c.z - pos.z);
      if (d < bestD) { best = c; bestD = d; }
    }
    return best;
  }

  corpseLabel() { return 'Search the body'; }

  // deterministic contents — every client rolls the same pockets from the id
  corpseLoot(c) {
    const table = CORPSE_TABLES[c.tag] || CORPSE_TABLES.civilian;
    if (hashId(c.id, 201) < CORPSE.emptyChance) return [];
    let n = 1;
    if (hashId(c.id, 202) < CORPSE.second) n++;
    if (hashId(c.id, 203) < CORPSE.third) n++;
    const tw = table.reduce((s, e) => s + e[1], 0);
    const out = [];
    for (let k = 0; k < n; k++) {
      let r = hashId(c.id, 210 + k) * tw;
      const pickEntry = table.find((e) => (r -= e[1]) <= 0) || table[0];
      const id = pickEntry[0];
      if (!ITEMS[id]) continue;
      const qty = ITEMS[id].stack > 1 && hashId(c.id, 220 + k) < 0.35 ? 2 : 1;
      const at = out.find((o) => o.id === id);
      if (at) at.qty += qty; else out.push({ id, qty });
    }
    return out;
  }

  searchCorpse(c, inventory) {
    if (!c || this.searched.has(c.id)) return { ok: false, msg: 'Nothing left on the body' };
    // a half-searched body remembers what is still in its pockets, so the
    // remainder is neither destroyed nor re-rolled into a duplicate
    const loot = this.corpseLeft.get(c.id) || (this.partial.has(c.id) ? [] : this.corpseLoot(c));
    const done = () => {
      this.searched.add(c.id);
      this.corpseLeft.delete(c.id);
      this.partial.delete(c.id);
      this.noise?.emit('rustle', c.x, c.z, { r: CORPSE.noise });
      this.onNet?.({ k: 'corpse-search', id: c.id });
    };
    if (!loot.length) {
      done();
      return { ok: true, msg: 'Nothing but a torn shirt' };
    }
    const got = [];
    const left = [];
    for (const l of loot) {
      // addItem is all-or-nothing per stack now, so a false here really means
      // the stack is untouched and can honestly stay on the body (A2)
      if (left.length || !inventory.addItem(l.id, l.qty)) { left.push(l); continue; }
      got.push(`${ITEMS[l.id].name}${l.qty > 1 ? ' ×' + l.qty : ''}`);
    }
    if (!got.length) return { ok: false, msg: 'Inventory full' };
    this.noise?.emit('rustle', c.x, c.z, { r: CORPSE.noise });
    if (left.length) {
      this.corpseLeft.set(c.id, left);
      this.partial.add(c.id);
      // others must still see the body as searched (that is today's contract);
      // what is left is ours to come back for
      this.onNet?.({ k: 'corpse-search', id: c.id });
      return { ok: true, msg: `Took ${got.join(', ')} — more still on the body` };
    }
    done();
    return { ok: true, msg: `Searched the body — ${got.join(', ')}` };
  }

  // multiplayer: someone else got there first
  applyCorpseSearch(id) {
    this.searched.add(id);
    this.corpseLeft.delete(id);
    this.partial.delete(id);
  }

  update(focus, dt = 0) {
    if (!this.tables) return; // tables still fetching
    this._sweepRespawn(focus, dt || 1 / 60);
    if (Math.hypot(focus.x - this.lastBuild.x, focus.z - this.lastBuild.z) > 60) {
      this.lastBuild.copyFrom(focus);
      this.rebuild(focus);
    }
  }
}
